import React from 'react';
import { useTranslation } from 'react-i18next';
import { CouponForm, SettingsModel } from '../../models/api/codegen/code';
import { useDispatch, useSelector } from 'react-redux';
import {
    getPlayerSettings,
    HIDE_NEED_CONFIRM_DIALOG,
    savePlayerSettingsAsync
} from '../../store/spirs/actions';
import { StoreType } from '../../store/spirs/types';
import * as Popover from '@radix-ui/react-popover';
import Numpad from '../numpad/Numpad';
import { SwitchWithText } from '../../utils/SwitchWithText';

interface SettingsProps {
    buttonGroupByColumn: boolean;
    hideMainTitle?: boolean;
}

export function SettingsInner(props: SettingsProps) {
    const dispatch = useDispatch();
    const playerId = useSelector(
        (state: StoreType) => state.spirs.playerInfo.playerId
    );
    const settings = useSelector(
        (state: StoreType) => state.spirs.playerSettings
    );
    const isWeb = useSelector((state: StoreType) => state.spirs.playerInfo.web);
    React.useEffect(() => {
        dispatch(getPlayerSettings.request({ playerId }));
    }, [playerId]);
    const { t } = useTranslation();
    const save = (s: SettingsModel) => {
        dispatch(savePlayerSettingsAsync.request({ model: s }));
    };
    const handleChangeAccept = (v: CouponForm['acceptType']) => () =>
        save({ ...settings, acceptType: v });
    const handleChangeDefault = ({
        target: { name, value }
    }: React.ChangeEvent<HTMLInputElement>) =>
        save({
            ...settings,
            [name]: value
        });
    const handleChangeNeedConfirm = ({
        target: { checked }
    }: React.ChangeEvent<HTMLInputElement>) => {
        !checked && dispatch(HIDE_NEED_CONFIRM_DIALOG());
        save({
            ...settings,
            needConfirm: checked
        });
    };

    const [openNumpad, setOpenNumpad] = React.useState<boolean>(false);
    const [currentInput, setCurrentInput] = React.useState<
        'defaultLive' | 'defaultLine'
    >('defaultLive');

    function openNumpud(
        e: React.MouseEvent<HTMLInputElement>,
        cInput: 'defaultLive' | 'defaultLine'
    ) {
        setOpenNumpad(true);
        setCurrentInput(cInput);
    }

    function updateInput(value: string) {
        save({ ...settings, [currentInput]: Number(`${settings[currentInput]}${value}`) });
    }

    function resetInput() {
        save({ ...settings, [currentInput]: 0 });
    }

    function delDigit() {
        if (!settings || !currentInput) return;

        const value = settings[currentInput];
        if (value === undefined || value <= 0) return;

        save({ ...settings, [currentInput]: Math.floor(value / 10) });
    }

    return (
        <div className="bg-white text-black max-w-[314px] tablet:max-w-[433px] rounded-[12px] shadow-[0_0_16px_4px_rgba(0,0,0,0.5)]">
            {!props.hideMainTitle && (
                <React.Fragment>
                    <div className="text-[16px] tablet:text-[20px] text-[#495159] font-medium pl-[14px] tablet:pl-[23px] pt-[18px]">
                        {t('settings.settingsMainTitle')}
                    </div>
                    <div className={'mt-[15px] h-[2px] bg-[#E1F2FD] w-full'} />
                </React.Fragment>
            )}

            <div className="p-[12px] text-[#484C56] text-[16px] font-normal">
                <div className="pl-[11px] text-[14px] tablet:text-[18px] m-b-[17px] font-medium">
                    {t('settings.couponTitle')}
                </div>
                <div className="pl-[11px] flex justify-between items-center pb-[16px] max-tablet:text-[12px]">
                    {t('settings.askPermission')}
                    <div className={'scale-60'}>
                        <SwitchWithText
                            checked={settings.needConfirm ?? false}
                            onChange={handleChangeNeedConfirm}
                        />
                    </div>
                </div>
                <div className="pl-[11px] flex justify-between items-center pb-[16px] max-tablet:text-[12px]">
                    <span className="text-[14px] font-[Roboto] leading-4 text-[#484C56]">
                        {t("settings.defaultStepLive")}
                    </span>
                    <input
                        className="w-[72px] h-[25px] tablet:w-[139px] tablet:h-[38px] rounded-[8px] border-[1px] border-solid border-[#B8BBC6] shadow-[0_1px_2px_0px_rgba(0,0,0,0.10)] bg-white"
                        maxLength={15}
                        name={'defaultLive'}
                        onClick={(e: React.MouseEvent<HTMLInputElement>) =>
                            openNumpud(e, 'defaultLive')
                        }
                        // onChange={handleChangeDefault}
                        onChange={handleChangeDefault}
                        value={settings.defaultLive}
                    />
                </div>
                <div className="pl-[11px] flex justify-between items-center pb-[16px] max-tablet:text-[12px]">
                    <span className="text-[14px] font-[Roboto] leading-4 text-[#484C56]">
                        {t('settings.defaultStepLine')}
                    </span>
                    <input
                        className="w-[72px] h-[25px] tablet:w-[139px] tablet:h-[38px] rounded-[8px] border-[1px] border-solid border-[#B8BBC6] shadow-[0_1px_2px_0px_rgba(0,0,0,0.10)] bg-white"
                        maxLength={15}
                        name={'defaultLine'}
                        onClick={(e: React.MouseEvent<HTMLInputElement>) =>
                            openNumpud(e, 'defaultLine')
                        }
                        onChange={handleChangeDefault}
                        value={settings.defaultLine}
                    />
                </div>
                <Popover.Root
                    open={openNumpad && !isWeb}
                    onOpenChange={v => setOpenNumpad(v)}
                >
                    <Popover.Trigger>
                        <div />
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content sideOffset={10} style={{ zIndex: 9999 }}>
                            {currentInput && (
                                <Numpad
                                    addDigit={updateInput}
                                    delDigit={delDigit}
                                    resetValue={resetInput}
                                />
                            )}
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>

                <div className={'mb-[15px] h-[2px] bg-[#E1F2FD] w-full'} />

                <div className="pl-[11px] pb-[9px] tablet:pb-[16px] max-tablet:text-[12px]">
                    {t('settings.whichCoefficientTakeMessage')}
                </div>

                <div
                    className={`flex ${
                        props.buttonGroupByColumn ? 'flex-col' : 'flex-row'
                    }`}
                >
                    <button
                        className={`max-tablet:text-[12px] max-tablet:font-medium h-[25px] tablet:h-[38px] border-[1px] border-solid border-[#B8BBC6] shadow-[0_1px_2px_0px_rgba(0,0,0,0.10)] w-full p-y-[10px] p-x-[11px] ${
                            props.buttonGroupByColumn
                                ? 'rounded-t-[8px] border-b-0'
                                : 'rounded-l-[8px]'
                        } ${
                            settings.acceptType === 'DO_NOT_ACCEPT_CHANGES'
                                ? 'bg-[#DB6263] border-[#DB6263] text-white'
                                : ''
                        }`}
                        onClick={handleChangeAccept('DO_NOT_ACCEPT_CHANGES')}
                    >
                        {t('settings.autoCoefficient.dontTake')}
                    </button>
                    <button
                        className={`max-tablet:text-[12px] max-tablet:font-medium h-[25px] tablet:h-[38px] border-solid border-[#B8BBC6] shadow-[0_1px_2px_0px_rgba(0,0,0,0.10)] w-full p-y-[10px] p-x-[11px] ${
                            props.buttonGroupByColumn
                                ? 'border-x-[1px] border-y-0'
                                : 'border-y-[1px] border-solid border-[#B8BBC6]'
                        } ${
                            settings.acceptType === 'ACCEPT_PROMOTION'
                                ? 'bg-[#DB6263] border-[#DB6263] text-white'
                                : ''
                        }`}
                        onClick={handleChangeAccept('ACCEPT_PROMOTION')}
                    >
                        {t('settings.autoCoefficient.bigger')}
                    </button>
                    <button
                        className={`max-tablet:text-[12px] max-tablet:font-medium h-[25px] tablet:h-[38px] border-[1px] border-solid border-[#B8BBC6] shadow-[0_1px_2px_0px_rgba(0,0,0,0.10)] w-full p-y-[10px] p-x-[11px] ${
                            props.buttonGroupByColumn
                                ? 'rounded-b-[8px] border-t-0'
                                : 'rounded-r-[8px]'
                        } ${
                            settings.acceptType === 'ACCEPT_ANY_CHANGES'
                                ? 'bg-[#DB6263] border-[#DB6263] text-white'
                                : ''
                        }`}
                        onClick={handleChangeAccept('ACCEPT_ANY_CHANGES')}
                    >
                        {t('settings.autoCoefficient.any')}
                    </button>
                </div>
            </div>
        </div>
    );
}
